import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import Null_player from "../../../assets/imgs/null-player-img.svg";
import Small_polygon from "../../../assets/imgs/small_polygon.svg";
import polygon from "../../../assets/imgs/polygon.svg";

import sortIcon from "../../../assets/imgs/sort_icon.svg";
import { Paragraph } from "../Paragraph";
import { teamColors } from "../../../config/constants/teamColors";
import { useNavigate } from "react-router-dom";
import { playerProfileCountViews } from "../../../Controllers/PlayerProfileCache";
import { fetchFieldValue, mapWithFallback } from "../../../lib/utils";
import { actions } from "../../../Redux/store";

interface TableHeaderItem {
  headerType: string;
  position?: string;
  headers: string[];
}

interface DataItem {
  name: string;
  firstName?: string;
  lastName?: string;
  position: string;
  Opponent?: string;
  consensus?: number | null;
  consensus_pos?: string;
  bye?: number | null;
  team?: string;
  total_ppr_fantasy_points_scored?: number | null;
  FPTS_PPR?: number | null;
  url?: string;
  pass_attempts?: number | null;
  completions?: number | null;
  passing_yards?: number | null;
  passing_tds?: number | null;
  interceptions?: number | null;
  passQBRating?: number | null;
  carries?: number | null;
  rushing_yards?: number | null;
  yards_per_rush_attempt?: number | null;
  rushing_tds?: number | null;
  targets?: number | null;
  receptions?: number | null;
  yards_per_reception?: number | null;
  receiving_tds?: number | null;
  trade_value?: number | null;
  roto_player_id?: string;
  [key: string]: any;
}

interface Category {
  name: string;
  id: number;
}

interface PositionItem {
  id: number;
  name: string;
}

interface Props {
  tableHeader: TableHeaderItem[];
  data: DataItem[];
  category: Category;
  positionSelected: PositionItem;
  onSentEventFirebase: (name: string, params?: { [key: string]: any }) => void;
  posID?: number;
  perID?: number;
  togglePlayerProfile?: any;
  tableSorte?: any;
}

const TableCom: React.FC<Props> = ({
  tableHeader,
  data,
  category,
  positionSelected,
  onSentEventFirebase,
  posID,
  perID,
  togglePlayerProfile,
  tableSorte,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null,
  });

  // Add window width state to track screen size
  const [deviceWidth, setDeviceWidth] = useState<number>(window.innerWidth);
  // Add state to track if player image column should be visible
  const [showPlayerImageColumn, setShowPlayerImageColumn] = useState<boolean>(false);
  // Ref for the scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use more reliable resize detection for iOS WebView
  useEffect(() => {
    // Get initial width
    setDeviceWidth(window.innerWidth);

    // Handle resize with a small delay to improve iOS WebView reliability
    const handleResize = () => {
      // Small timeout to ensure iOS WebView has updated dimensions
      setTimeout(() => {
        setDeviceWidth(window.innerWidth);
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    // iOS specific - also listen for orientation change events
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const navigate = useNavigate();

  const findHeadersByTypeAndPosition = (
    headerType: string,
    position: string,
  ) => {
    if (position === "All") {
      return tableHeader.find((element) => element.headerType === headerType);
    }
    const headers = tableHeader.find(
      (element) => element.headerType === headerType,
    );
    return headers
      ? headers
      : tableHeader.find((element) => element.headerType === headerType);
  };

  const specificHeaders = findHeadersByTypeAndPosition(category.name, "");

  // Calculate column configuration early
  const hasMoreThanThreeColumns =
    specificHeaders?.headers && specificHeaders.headers.length > 3;

  // Handle scroll detection to show/hide player image column
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollLeft = scrollContainerRef.current.scrollLeft;
        // Calculate threshold based on the second column (player name column) going out of view
        // Index column: ~50px + Player name column: ~270px = ~320px total
        // We want to show the player image column when the second column is completely scrolled out
        const indexColumnWidth = 50;
        const playerNameColumnWidth = hasMoreThanThreeColumns ? 250 : 260; // Use the same width logic as in the table
        const threshold = indexColumnWidth + playerNameColumnWidth; // Show when 100% of player name column is scrolled out
        setShowPlayerImageColumn(scrollLeft > threshold);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [data, hasMoreThanThreeColumns]); // Re-run when data or column configuration changes

  const fetchFieldName = (item: DataItem) => {
    return item.playerName ? item.playerName : null;
  };

  const onClickCell = async (playerId: string, playerName: string) => {
    if (togglePlayerProfile) {
      actions.modal.openPlayerProfileModal({ playerId: playerId });
      onSentEventFirebase("rankings_player_selected", {
        playerID: playerId,
        playerName,
      });
    }
  };

  // Sorting functions
  const getFieldValueForSort = (
    item: DataItem,
    header: string,
    index: number,
  ) => {
    if (header === "Player") {
      return fetchFieldName(item) || "";
    }

    if (header === specificHeaders?.headers[0]) {
      // First column - use index for sorting rather than displayed value
      return index;
    }

    const value = fetchFieldValue(item, header);
    return value === null ? -Infinity : value;
  };

  const handleSort = (header: string) => {
    let direction: "asc" | "desc" | null = "asc";

    if (sortConfig.key === header) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null;
      }
    }

    setSortConfig({ key: header, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key || sortConfig.direction === null) {
      return data;
    }

    return [...data].sort((a, b) => {
      const indexA = data.indexOf(a);
      const indexB = data.indexOf(b);

      // Special handling for the first column (index column)
      if (sortConfig.key === specificHeaders?.headers[0]) {
        // For index column, we use the original position in the array
        return sortConfig.direction === "asc"
          ? indexA - indexB
          : indexB - indexA;
      }

      const aValue = getFieldValueForSort(a, sortConfig.key, indexA);
      const bValue = getFieldValueForSort(b, sortConfig.key, indexB);

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIconStyle = (header: string) => {
    if (sortConfig.key !== header) return "transform rotate-0 opacity-50";
    if (sortConfig.direction === "asc") return "transform rotate-180";
    if (sortConfig.direction === "desc") return "transform rotate-0";
    return "transform rotate-0 opacity-50";
  };

  const sortedData = getSortedData();

  // Determine if scrolling should be enabled
  const isSmallScreen = deviceWidth < 375;
  const shouldEnableScrolling = hasMoreThanThreeColumns || isSmallScreen;

  return (
    <div
      ref={scrollContainerRef}
      className={`${shouldEnableScrolling ? "overflow-x-auto table-scroll-container" : "overflow-x-hidden"} w-full px-2`}
    >
      {data?.length > 0 ? (
        <Table
          className={`w-full ${!shouldEnableScrolling ? "table-fixed" : ""}`}
        >
          <TableHeader>
            <TableRow className="hover:bg-transparent border-transparent">
              {/* Player Image Column - Conditionally visible and sticky with smooth fade animation */}
              <AnimatePresence>
                {showPlayerImageColumn && (
                  <motion.th
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ 
                      duration: 0.3, 
                      ease: "easeInOut" 
                    }}
                    className="text-white/70 text-[14px] pb-2 sticky left-[-10px] bg-transparent  z-10 "
                    style={{
                      width: "auto",
                      minWidth: "90px",
                      maxWidth: "120px",
                    }}
                  >
                    <div className="flex items-center justify-start gap-2 font-medium ">
                      <span className="bg-[#000513] p-[10px] h-full ">
                        Player
                      </span>
                      
                    </div>
                  </motion.th>
                )}
              </AnimatePresence>

              {specificHeaders?.headers &&
                specificHeaders.headers.map((item, index) => (
                  <TableHead
                    key={"players-header-" + index}
                    className={`text-white/70 text-[14px] pb-2 ${tableSorte ? "cursor-pointer" : ""}`}
                    style={{
                      width:
                        index === 0
                          ? "50px"
                          : index === 1
                            ? hasMoreThanThreeColumns
                              ? "270px"
                              : "auto"
                            : category?.id === 1
                              ? "118px"
                              : "110px",
                      minWidth:
                        index === 0
                          ? "50px"
                          : index === 1
                            ? "270px"
                            : category?.id === 1
                              ? "118px"
                              : "110px",
                    }}
                    onClick={() => tableSorte && handleSort(item)}
                  >
                    <div
                      className={`flex items-center ${category?.id === 2 && index !== 0 && index !== 1 ? "justify-center" : "justify-start"} gap-2`}
                    >
                      {item}
                      {tableSorte && (
                        <img
                          src={sortIcon}
                          alt="sortIcon"
                          className={`transition-all duration-200 ${getSortIconStyle(item)}`}
                        />
                      )}
                    </div>
                  </TableHead>
                ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {mapWithFallback(sortedData).map((item, index) => (
              <TableRow
                key={"player-body-" + item.roto_player_id}
                className={`cursor-pointer hover:bg-transparent border-transparent bg-transparent text-white`}
                onClick={() =>
                  onClickCell(
                    item.roto_player_id as string,
                    fetchFieldName(item) as string,
                  )
                }
              >
                {/* Player Image Column - Conditionally visible and sticky with smooth fade animation */}
                <AnimatePresence>
                  {showPlayerImageColumn && (
                    <motion.td
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ 
                        duration: 0.3, 
                        ease: "easeInOut" 
                      }}
                      className=" sticky left-[-10px] bg-transparent z-10 " 
                      style={{ 
                        width: "auto",
                        minWidth: "90px",
                        maxWidth: "120px"
                      }}
                    >
                      <div className="flex gap-3">
                        {/* Player Image */}
                        <div className="relative bg-[#000513] pl-[10px]">
                          <div
                            className="rounded-xl w-[44px] h-[44px] flex items-end overflow-hidden"
                            style={{
                              backgroundColor: item.playerTeamShort
                                ? teamColors[item.playerTeamShort]
                                : "#4F2683",
                            }}
                          >
                            <img
                              id={item.roto_player_id + "_img_col"}
                              key={item.roto_player_id + "_img_col"}
                              className={
                                item.playerHeadshotURL ? "scale-[1.88]" : "h-[45px]"
                              }
                              src={item.playerHeadshotURL || Null_player}
                              alt=""
                            />
                          </div>
                          <div className="absolute -bottom-0 -right-[11.5px]">
                            <div className="relative">
                              <img
                                src={polygon}
                                alt=""
                                className="w-[23px] h-[23px]"
                              />
                              <Paragraph
                                text={item.playerPosition}
                                props={{
                                  fontSize: "7px",
                                  lineHeight: "100%",
                                  fontWeight: "400",
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                }}
                                ParagraphType="Pragragh22"
                                color="textSecundary"
                              />
                            </div>
                          </div>
                        </div>

                     
                          <span className="text-[10px] font-medium bg-white/10 rounded-[4px] px-1 mt-[-12px] h-fit whitespace-nowrap" title={item?.playerNameAbbreviated}>
                            {item?.playerNameAbbreviated}
                          </span>
               
                      </div>
                    </motion.td>
                  )}
                </AnimatePresence>

                <TableCell className="pr-2" style={{ width: "50px" }}>
                  <div className="flex items-center gap-1.5">
                    <div className="flex-shrink-0 text-xs font-semibold w-fit">
                      {item.performance_rank != null
                        ? item.performance_rank
                        : sortConfig.key === specificHeaders?.headers[0] &&
                            sortConfig.direction === "desc"
                          ? data.length - index
                          : index + 1}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  key={"player-cell-name-" + item.roto_player_id}
                  className="text-base font-medium flex flex-col gap-1"
                  style={{
                    width: hasMoreThanThreeColumns ? "270px" : "auto",
                    maxWidth: hasMoreThanThreeColumns ? "270px" : "none",
                    minWidth: "260px", // Set minimum width for small screens
                  }}
                >
                  <div className="flex items-center gap-[26px]">
                    <div className="relative">
                      <div
                        className="rounded-xl w-[44px] h-[44px] flex items-end overflow-hidden"
                        style={{
                          backgroundColor: item.playerTeamShort
                            ? teamColors[item.playerTeamShort]
                            : "#4F2683",
                        }}
                      >
                        <img
                          id={item.roto_player_id}
                          key={item.roto_player_id}
                          className={
                            item.playerHeadshotURL ? "scale-[1.88]" : "h-[45px]"
                          }
                          src={item.playerHeadshotURL || Null_player}
                          alt=""
                        />
                      </div>
                      <div className="absolute -bottom-0 -right-[11.5px]">
                        <div className="relative">
                          <img
                            src={polygon}
                            alt=""
                            className="w-[23px] h-[23px]"
                          />
                          <Paragraph
                            text={item.playerPosition}
                            props={{
                              fontSize: "7px",
                              lineHeight: "100%",
                              fontWeight: "400",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                            ParagraphType="Pragragh22"
                            color="textSecundary"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="ml-1 text-[16px]">
                      {fetchFieldName(item)}
                      <div className="text-[12px] font-medium opacity-50 mt-[2px] leading-[18px] ">
                        {item.playerTeamShort}
                      </div>
                    </div>
                  </div>
                </TableCell>

                {specificHeaders?.headers.slice(2).map((header, idx) => (
                  <TableCell
                    key={`cell-${index}-${idx}`}
                    style={{ textAlign: category?.id === 2 ? "center" : "end" }}
                  >
                    {fetchFieldValue(item, header)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex justify-center mt-10">
          <Paragraph
            text={"--No Data Currently--"}
            props={{
              fontSize: "20px",
              lineHeight: "145%",
              letterSpacing: "-0.4px",
              opacity: "0.70",
            }}
            ParagraphType="Pragragh22"
            color="textPrimary"
          />
        </div>
      )}
    </div>
  );
};

export default TableCom;
